/**
 * useMe — richer `/auth/me` adapter for admin-portal surfaces.
 *
 * The admin shell + every Companies/Staff/Imports surface needs a
 * unified view of:
 *
 *   user     — { id, email, name? }
 *   platform — { role: PlatformRole } | null (platform-staff record;
 *              null for non-platform users)
 *   portals  — PortalId[] (entitlements the user has access to)
 *
 * The shared `useCurrentUser()` hook surfaces only `{ id, email }` —
 * enough for the existing dashboard greeting. `useMe()` extends that
 * with the platform + portal entitlement projection the admin portal
 * needs.
 *
 * **`portals` semantics.** SCRUM-131 backend now ships
 * `AuthMeResponseDto.portals` as a REQUIRED, always-present array
 * (possibly empty). There is no longer a tri-state null sentinel:
 * a missing or malformed payload simply normalizes to `[]`, and an
 * empty array means the user genuinely has zero entitlements.
 *
 * Future-shape tolerance: this hook honors BOTH the current flat DTO
 * (top-level `id` + `email`) AND a nested `user.{id, name, email}`
 * shape. The dual extraction keeps the route honest on every backend
 * state during rollout.
 */

import * as React from 'react';
import { useSession } from '../hooks/useSession.js';
import { isPlatformRole, type PlatformRole } from './admin-permissions.js';

export interface MePlatform {
  readonly role: PlatformRole;
}

export interface MeUser {
  readonly id: string;
  readonly email: string;
  /** Optional today; backend may ship it under either shape. */
  readonly name?: string;
}

export type MePortalId = 'PMS' | 'MSA' | 'Admin';
const ME_PORTAL_IDS: ReadonlyArray<MePortalId> = ['PMS', 'MSA', 'Admin'];

export interface Me {
  readonly user: MeUser;
  /** Platform-staff record, or `null` for non-platform users. */
  readonly platform: MePlatform | null;
  /**
   * Portal entitlements. The contract now guarantees a (possibly-empty)
   * array; consumers never see `null`.
   */
  readonly portals: ReadonlyArray<MePortalId>;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function extractUser(raw: Record<string, unknown>): MeUser | null {
  // Nested shape: `user: { id, email, name?, avatar_url? }`.
  const nested = raw.user;
  if (isPlainObject(nested)) {
    const id = nested.id;
    const email = nested.email;
    if (typeof id === 'string' && typeof email === 'string') {
      const name = nested.name;
      return {
        id,
        email,
        ...(typeof name === 'string' ? { name } : {}),
      };
    }
  }
  // Current flat DTO: top-level `id` + `email`, no `name` field.
  const flatId = raw.id;
  const flatEmail = raw.email;
  if (typeof flatId === 'string' && typeof flatEmail === 'string') {
    return { id: flatId, email: flatEmail };
  }
  return null;
}

function extractPlatform(raw: Record<string, unknown>): MePlatform | null {
  const platformRaw = raw.platform;
  if (!isPlainObject(platformRaw)) return null;
  const role = platformRaw.role;
  if (!isPlatformRole(role)) return null;
  return { role };
}

function extractPortals(
  raw: Record<string, unknown>,
): ReadonlyArray<MePortalId> {
  const portalsRaw = raw.portals;
  // The contract guarantees an array; any non-array payload (missing,
  // malformed `portals: "PMS"`, etc.) normalizes to the empty array.
  if (!Array.isArray(portalsRaw)) return [];
  const out: MePortalId[] = [];
  for (const entry of portalsRaw) {
    // Accept either a bare string id or `{ id: PortalId, context }`
    // (forward-compat).
    if (
      typeof entry === 'string' &&
      (ME_PORTAL_IDS as readonly string[]).includes(entry)
    ) {
      out.push(entry as MePortalId);
      continue;
    }
    if (isPlainObject(entry)) {
      const id = entry.id;
      if (
        typeof id === 'string' &&
        (ME_PORTAL_IDS as readonly string[]).includes(id)
      ) {
        out.push(id as MePortalId);
      }
    }
  }
  return out;
}

/**
 * Returns the unified `Me` projection, or `null` when the session is
 * not yet authenticated / errored / shape-incompatible.
 */
export function useMe(): Me | null {
  const session = useSession();
  return React.useMemo<Me | null>(() => {
    if (session.status !== 'authenticated') return null;
    if (!isPlainObject(session.user)) return null;
    const user = extractUser(session.user);
    if (user === null) return null;
    const platform = extractPlatform(session.user);
    const portals = extractPortals(session.user);
    return {
      user,
      platform,
      portals,
    };
  }, [session.status, session.user]);
}
